/**
 * Confirmed hotel listings vs legacy auto-verified fallbacks.
 * Inputs: a stored place. Outputs: whether to repair, unverify, preserve, or leave unchanged.
 * Coordinates come from identifiable listings, not from a Nominatim miss.
 */

import type { Place, TripDocument } from "./schema.ts";

export const HOTEL_PLACE_IDS = ["narinthra", "chantra", "vimarn", "centre-point"] as const;
export type HotelPlaceId = (typeof HOTEL_PLACE_IDS)[number];

export const LEGACY_HOTEL_FALLBACKS: Record<HotelPlaceId, { lat: number; lng: number }> = {
  narinthra: { lat: 18.78785, lng: 98.98172 },
  chantra: { lat: 18.68715, lng: 98.91938 },
  vimarn: { lat: 12.56792, lng: 101.45688 },
  "centre-point": { lat: 13.72405, lng: 100.52982 },
};

const FALLBACK_SOURCE_MARKERS = ["Published OSM/map listing", "after Nominatim miss"];
const CONFIRMED_SOURCE_MARKER = "Confirmed listing 2026-09-27";
const AUTOMATIC_SOURCE_MARKERS = ["OpenStreetMap Nominatim:", ...FALLBACK_SOURCE_MARKERS];

export type ConfirmedHotel = {
  id: HotelPlaceId;
  identityName: string;
  identityTokens: string[];
  lat: number;
  lng: number;
  mapsUrl: string;
  precision: string;
  source: string;
  sourceUrls: string[];
};

/**
 * Identity, address and coordinates recorded 27 Sep 2026.
 * Listing locations, not surveyed hotel-entrance nodes, unless an OSM POI exists.
 */
export const CONFIRMED_HOTELS: Record<HotelPlaceId, ConfirmedHotel> = {
  narinthra: {
    id: "narinthra",
    identityName: "Narinthra Heritage",
    identityTokens: ["narinthra"],
    lat: 18.787015,
    lng: 98.991374,
    mapsUrl: "https://www.google.com/maps/search/?api=1&query=Narinthra%20Heritage%2018.787015%2C98.991374",
    precision: "published_listing_not_surveyed_entrance",
    source: `${CONFIRMED_SOURCE_MARKER}: Narinthra Heritage, 3 Ratchadamnoen Road Soi 4, Tambon Phra Singh, Chiang Mai 50200. Official site identity/address near Tha Phae Gate; iBooked listing 18.787015, 98.991374; OSM way 130819448 (Ratchadamnoen Road Soi 4) at 18.7860072, 98.9914766. Not a surveyed entrance.`,
    sourceUrls: [
      "https://www.narinthraheritage.com/",
      "https://narinthra-heritage-chiang-mai.ibooked.ca/",
      "https://www.openstreetmap.org/way/130819448",
    ],
  },
  chantra: {
    id: "chantra",
    identityName: "Chantra Khiri Chalet",
    identityTokens: ["chantra"],
    lat: 18.78269,
    lng: 98.841774,
    mapsUrl: "https://www.google.com/maps/search/?api=1&query=Chantra%20Khiri%20Chalet%2018.782690%2C98.841774",
    precision: "osm_property_listing",
    source: `${CONFIRMED_SOURCE_MARKER}: Chantra Khiri Chalet, 49 Moo 3, Baan Pong, Hang Dong, Chiang Mai 50230. Official site identity/address; OSM node 5372554721 (named Chantra Khiri Chalet) 18.782690, 98.841774. Google Maps hotel listing 18.783136, 98.841876 and Vive Hotels 18.78328, 98.841671 corroborate the same campus (~50 m). OSM tags the node as restaurant (on-site). Not a surveyed vehicle entrance.`,
    sourceUrls: [
      "https://chantrakhirichalet.com/",
      "https://www.openstreetmap.org/node/5372554721",
      "https://resortchantrakhirichalet.vivehotels.com/en/location/",
    ],
  },
  vimarn: {
    id: "vimarn",
    identityName: "Vimarn Samed Resort",
    identityTokens: ["vimarn"],
    lat: 12.5548474,
    lng: 101.4533074,
    mapsUrl: "https://www.google.com/maps/search/?api=1&query=Vimarnsamed%20Resort%2012.5548474%2C101.4533074",
    precision: "osm_property_listing",
    source: `${CONFIRMED_SOURCE_MARKER}: Vimarn Samed Resort, 13/18 Moo 4, Ao Wongduan, Ko Samet, Rayong 21160. Booking.com/Agoda identity and Wongdeuan address; OSM node 6014816462 (Vimarnsamed Resort) 12.5548474, 101.4533074 in Ao Wongdeuan. Nominatim's first hit for 'Vimarn Samed' is a different hotel (Le Vimarn).`,
    sourceUrls: [
      "https://www.booking.com/hotel/th/vimarn-samed-resort.html",
      "https://www.openstreetmap.org/node/6014816462",
    ],
  },
  "centre-point": {
    id: "centre-point",
    identityName: "Centre Point Plus Hotel Silom",
    identityTokens: ["centre point"],
    lat: 13.7197325,
    lng: 100.5150903,
    mapsUrl: "https://www.google.com/maps/search/?api=1&query=Centre%20Point%20Silom%2013.7197325%2C100.5150903",
    precision: "osm_building_on_named_soi",
    source: `${CONFIRMED_SOURCE_MARKER}: Centre Point Plus Hotel Silom, 1522/2 Soi Kaysorn 1 (Charoenkrung 50), Bang Rak, Bangkok 10500. Official site identity/address; OSM way 856462919 (Center Point Silom building) on Charoenkrung 50 at 13.7197325, 100.5150903. Official page also prints 13.7196272, 100.5128673 (~240 m west); the pin follows the mapped building on the named soi. Not a surveyed entrance.`,
    sourceUrls: [
      "https://www.centrepoint.com/silom",
      "https://www.openstreetmap.org/way/856462919",
    ],
  },
};

export type PlaceRepairAction = "repaired" | "unverified" | "preserved" | "unchanged" | "review" | "confirmed";

export type PlaceRepairRow = {
  id: string;
  name: string;
  action: PlaceRepairAction;
  detail: string;
};

const COORD_EPS = 0.00005;

export function coordsMatch(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
  eps = COORD_EPS,
): boolean {
  return Math.abs(a.lat - b.lat) <= eps && Math.abs(a.lng - b.lng) <= eps;
}

export function isHotelPlaceId(id: string): id is HotelPlaceId {
  return (HOTEL_PLACE_IDS as readonly string[]).includes(id);
}

export function hotelIdentityMatches(place: Place, hotel: ConfirmedHotel): boolean {
  const hay = `${place.name} ${place.address ?? ""}`.toLowerCase();
  return hotel.identityTokens.every((token) => hay.includes(token.toLowerCase()));
}

export function isLegacyFallbackPlace(place: Place): boolean {
  if (!isHotelPlaceId(place.id) || !place.coordinates) return false;
  const fallback = LEGACY_HOTEL_FALLBACKS[place.id];
  const src = place.coordinates.source ?? "";
  const sourceLegacy = FALLBACK_SOURCE_MARKERS.some((marker) => src.includes(marker));
  return sourceLegacy || coordsMatch(place.coordinates, fallback);
}

export function isAutomaticLookupSource(source: string | undefined): boolean {
  if (!source) return false;
  return AUTOMATIC_SOURCE_MARKERS.some((marker) => source.includes(marker));
}

export function isConfirmedListingSource(source: string | undefined): boolean {
  return Boolean(source?.includes(CONFIRMED_SOURCE_MARKER));
}

function applyConfirmed(place: Place, hotel: ConfirmedHotel, asOf: string): Place {
  const replaceMaps = !place.mapsUrl
    || (isHotelPlaceId(place.id) && place.mapsUrl.includes(String(LEGACY_HOTEL_FALLBACKS[place.id].lat)));
  return {
    ...place,
    coordinates: {
      lat: hotel.lat,
      lng: hotel.lng,
      verified: true,
      source: hotel.source,
      asOf,
    },
    mapsUrl: replaceMaps ? hotel.mapsUrl : place.mapsUrl,
    precision: hotel.precision,
    enrichmentStatus: "confirmed_listing",
  };
}

function unverify(place: Place, reason: string, asOf: string): Place {
  const coords = place.coordinates;
  return {
    ...place,
    coordinates: coords
      ? {
        lat: coords.lat,
        lng: coords.lng,
        verified: false,
        source: `${reason} (${asOf})`,
        asOf,
      }
      : null,
    enrichmentStatus: "unverified_listing",
  };
}

export function confirmedPlacePatch(place: Place, asOf: string): Place | null {
  if (!isHotelPlaceId(place.id)) return null;
  const hotel = CONFIRMED_HOTELS[place.id];
  if (!hotelIdentityMatches(place, hotel)) return null;
  return applyConfirmed(place, hotel, asOf);
}

/**
 * Idempotent repair of persisted fallback / automatic pins.
 * Does not overwrite the whole trip. Manual non-legacy corrections are kept.
 */
export function repairLegacyHotelPins(doc: TripDocument, asOf = "2026-09-27"): {
  doc: TripDocument;
  results: PlaceRepairRow[];
  changed: boolean;
} {
  const results: PlaceRepairRow[] = [];
  let changed = false;
  const places = doc.places.map((place) => {
    if (!isHotelPlaceId(place.id)) return place;
    const hotel = CONFIRMED_HOTELS[place.id];
    const coords = place.coordinates;

    if (coords?.verified && coordsMatch(coords, hotel) && isConfirmedListingSource(coords.source)) {
      results.push({ id: place.id, name: place.name, action: "unchanged", detail: "Already the confirmed listing." });
      return place;
    }

    const identity = hotelIdentityMatches(place, hotel);
    const legacy = isLegacyFallbackPlace(place);
    const automatic = isAutomaticLookupSource(coords?.source);

    if (identity && coords?.verified && coordsMatch(coords, hotel)) {
      const next = applyConfirmed(place, hotel, asOf);
      const same = JSON.stringify(next) === JSON.stringify(place);
      results.push({
        id: place.id,
        name: place.name,
        action: same ? "unchanged" : "repaired",
        detail: same ? "Already matches the confirmed listing." : "Aligned source text with the confirmed listing.",
      });
      if (!same) changed = true;
      return next;
    }

    if (legacy) {
      if (identity) {
        changed = true;
        results.push({
          id: place.id,
          name: place.name,
          action: "repaired",
          detail: `Replaced unsupported fallback with ${hotel.identityName}.`,
        });
        return applyConfirmed(place, hotel, asOf);
      }
      changed = true;
      results.push({
        id: place.id,
        name: place.name,
        action: "unverified",
        detail: "Removed a fallback pin that did not match this place's identity.",
      });
      return unverify(place, "Legacy fallback pin withdrawn; identity does not match the confirmed hotel", asOf);
    }

    if (automatic && identity) {
      changed = true;
      results.push({
        id: place.id,
        name: place.name,
        action: "repaired",
        detail: `Replaced an automatic lookup pin with ${hotel.identityName}.`,
      });
      return applyConfirmed(place, hotel, asOf);
    }

    if (automatic && !identity && coords?.verified) {
      changed = true;
      results.push({
        id: place.id,
        name: place.name,
        action: "unverified",
        detail: "Automatic lookup is not treated as confirmation for this place.",
      });
      return unverify(place, "Automatic lookup pin withdrawn", asOf);
    }

    if (coords?.verified && !automatic && !legacy && identity && !coordsMatch(coords, hotel)) {
      results.push({
        id: place.id,
        name: place.name,
        action: "preserved",
        detail: "Kept a later non-fallback pin. Flagged for owner review because it is not the 2026-09-27 confirmed listing.",
      });
      return place;
    }

    if (coords?.verified && !automatic && !legacy && !identity) {
      results.push({
        id: place.id,
        name: place.name,
        action: "review",
        detail: "Verified pin is not a known fallback and the name does not match the confirmed hotel. Left unchanged.",
      });
      return place;
    }

    results.push({ id: place.id, name: place.name, action: "unchanged", detail: "No fallback-derived pin to repair." });
    return place;
  });

  return { doc: changed ? { ...doc, places } : doc, results, changed };
}
